import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { db, auth, functions } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, updateDoc, increment, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { STokenLogo } from '../components/StreamDisplay';
import { useTheme } from '../contexts/ThemeContext';
import { useAdManager } from '../hooks/useAdManager';
import SlotMachine from '../components/games/SlotMachine';
import SpinWheel from '../components/games/SpinWheel';
import ScratchCard from '../components/games/ScratchCard';
import MemoryGame from '../components/games/MemoryGame';
import './Shop.css';
import './Marketplace.css';
import './Games.css';

// ── Map your RevenueCat package identifiers → stream amounts ──────────────
// Update these keys to match your dashboard package IDs exactly.
// If you added a "streams" field in RevenueCat product metadata, that takes
// priority and you won't need to touch this map.
const STREAM_MAP = {
  'streams_1000':  1000,
  'streams_5000':  5000,
  'streams_15000': 15000,
  'streams_50000': 50000,
  '$rc_monthly':   5000,
  '$rc_annual':    65000,
};

function getStreamsForPackage(pkg) {
  // 1. RevenueCat product metadata (set in dashboard under "Metadata" → "streams")
  const meta = pkg.product?.metadata;
  if (meta?.streams) return Number(meta.streams);
  // 2. Local map fallback
  return STREAM_MAP[pkg.identifier] ?? null;
}

function BuyStreamsTab() {
  const isNative = Capacitor.getPlatform() !== 'web';
  const [packages, setPackages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [buying, setBuying]       = useState(null);
  const [toast, setToast]         = useState(null);

  useEffect(() => {
    if (!isNative) { setLoading(false); return; }
    Purchases.getOfferings()
      .then(({ offerings }) => {
        const pkgs = offerings?.current?.availablePackages ?? [];
        setPackages(pkgs);
      })
      .catch(e => console.error('[BuyStreams]', e))
      .finally(() => setLoading(false));
  }, [isNative]);

  const handleBuy = useCallback(async (pkg) => {
    if (buying) return;
    setBuying(pkg.identifier);
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
      const streams = getStreamsForPackage(pkg);
      if (streams && auth.currentUser) {
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          totalStreams: increment(streams),
        });
      }
      setToast({ type: 'win', text: `+${streams?.toLocaleString() ?? '?'} Streams added!` });
      console.log('[BuyStreams] customerInfo', customerInfo);
    } catch (e) {
      if (e?.code !== 'PURCHASE_CANCELLED') {
        setToast({ type: 'err', text: 'Purchase failed. Please try again.' });
        console.error('[BuyStreams]', e);
      }
    } finally {
      setBuying(null);
      setTimeout(() => setToast(null), 3500);
    }
  }, [buying]);

  if (!isNative) {
    return (
      <div className="buy-streams__web-notice">
        <div className="buy-streams__web-icon">📱</div>
        <p className="buy-streams__web-title">Available in the app</p>
        <p className="buy-streams__web-body">In-app purchases are only available in the StreamStack Android app. Download it to buy Streams instantly.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="buy-streams__loading">Loading packages…</div>;
  }

  if (!packages.length) {
    return <div className="buy-streams__loading">No packages available right now.</div>;
  }

  return (
    <div className="buy-streams">
      {toast && (
        <div className={`buy-streams__toast buy-streams__toast--${toast.type}`}>{toast.text}</div>
      )}
      <div className="buy-streams__list">
        {packages.map(pkg => {
          const streams = getStreamsForPackage(pkg);
          const isBuying = buying === pkg.identifier;
          return (
            <div key={pkg.identifier} className="card buy-streams__card">
              <div className="buy-streams__card-left">
                <div className="buy-streams__card-title">{pkg.product.title}</div>
                {pkg.product.description ? (
                  <div className="buy-streams__card-desc">{pkg.product.description}</div>
                ) : null}
                {streams ? (
                  <div className="buy-streams__card-streams">
                    <STokenLogo size={13} />
                    <span>{streams.toLocaleString()} Streams</span>
                  </div>
                ) : null}
              </div>
              <button
                className="btn btn--primary buy-streams__card-btn"
                onClick={() => handleBuy(pkg)}
                disabled={!!buying}
              >
                {isBuying ? '…' : pkg.product.priceString}
              </button>
            </div>
          );
        })}
      </div>
      <p className="buy-streams__legal">Purchases are processed by Google Play and are non-refundable. Streams have no cash value.</p>
    </div>
  );
}

// ── Purchasable themes ────────────────────────────────────────────────────
const CUSTOM_THEMES = [
  {
    id: 'midnight',
    name: 'Midnight',
    desc: 'Electric blue on deep navy',
    bg: '#06101E',
    brand: '#00D4FF',
    accent: '#00A8E8',
    price: 1000,
  },
  {
    id: 'inferno',
    name: 'Inferno',
    desc: 'Blazing red on scorched black',
    bg: '#110604',
    brand: '#FF4500',
    accent: '#FF6B35',
    price: 1000,
  },
  {
    id: 'phantom',
    name: 'Phantom',
    desc: 'Ethereal purple on void black',
    bg: '#090612',
    brand: '#A855F7',
    accent: '#C084FC',
    price: 1000,
  },
];

function ThemesTab() {
  const { colorScheme, setColorScheme } = useTheme();
  const [userData, setUserData]   = useState(null);
  const [buying, setBuying]       = useState(null);
  const [toast, setToast]         = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => {
      setUserData(snap.data());
    });
    return unsub;
  }, []);

  const unlocked = userData?.unlockedThemes || [];
  const streams  = userData?.totalStreams   || 0;

  const buyTheme = useCallback(async (theme) => {
    if (buying) return;
    setBuying(theme.id);
    try {
      await httpsCallable(functions, 'buyTheme')({ themeId: theme.id });
      setColorScheme(theme.id);
      setToast({ type: 'win', text: `${theme.name} theme unlocked!` });
    } catch (e) {
      const msg = e?.message?.includes('NOT_ENOUGH_STREAMS') ? `Not enough Streams. Need ${theme.price.toLocaleString()}.`
        : e?.message?.includes('ALREADY_OWNED') ? 'Already owned.'
        : 'Purchase failed. Try again.';
      setToast({ type: 'err', text: msg });
    } finally {
      setBuying(null);
      setTimeout(() => setToast(null), 3000);
    }
  }, [buying, setColorScheme]);

  const applyTheme = (id) => {
    setColorScheme(id === colorScheme ? 'default' : id);
  };

  return (
    <div className="shop-themes">
      {toast && (
        <div className={`buy-streams__toast buy-streams__toast--${toast.type}`}>{toast.text}</div>
      )}

      {/* Default theme restore */}
      <button
        className={`shop-themes__default${colorScheme === 'default' ? ' shop-themes__default--active' : ''}`}
        onClick={() => setColorScheme('default')}
      >
        <span className="shop-themes__default-dot" />
        Default (Amber)
        {colorScheme === 'default' && <span className="shop-themes__active-badge">Active</span>}
      </button>

      <div className="shop-themes__grid">
        {CUSTOM_THEMES.map(theme => {
          const owned   = unlocked.includes(theme.id);
          const active  = colorScheme === theme.id;
          const loading = buying === theme.id;
          return (
            <div key={theme.id} className="card shop-themes__card">
              {/* Preview swatch */}
              <div className="shop-themes__preview" style={{ background: theme.bg }}>
                <div className="shop-themes__preview-bar" style={{ background: theme.brand }} />
                <div className="shop-themes__preview-dot" style={{ background: theme.accent }} />
                <div className="shop-themes__preview-line" style={{ background: `${theme.brand}44` }} />
                <div className="shop-themes__preview-line shop-themes__preview-line--short" style={{ background: `${theme.brand}33` }} />
              </div>

              <div className="shop-themes__card-body">
                <div className="shop-themes__card-name">{theme.name}</div>
                <div className="shop-themes__card-desc">{theme.desc}</div>
              </div>

              {owned ? (
                <button
                  className={`btn shop-themes__card-btn${active ? ' shop-themes__card-btn--active' : ''}`}
                  style={{ '--theme-brand': theme.brand }}
                  onClick={() => applyTheme(theme.id)}
                >
                  {active ? 'Active' : 'Apply'}
                </button>
              ) : (
                <button
                  className="btn btn--primary shop-themes__card-btn"
                  style={{ '--theme-brand': theme.brand }}
                  onClick={() => buyTheme(theme)}
                  disabled={!!buying}
                >
                  {loading ? '…' : (
                    <span style={{ display:'flex', alignItems:'center', gap:4, justifyContent:'center' }}>
                      {theme.price.toLocaleString()}<STokenLogo size={13} />
                    </span>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Custom Chest SVG Icons ────────────────────────────────────────────────────
// WoW / RuneScape-inspired, unique to StreamStack

function CommonChestIcon({ size = 64 }) {
  // Runescape-style: rough wooden planks, iron bands, brass lock
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Body */}
      <rect x="6" y="28" width="52" height="28" rx="3" fill="#7B4E2D"/>
      <rect x="6" y="28" width="52" height="28" rx="3" fill="url(#cw)" fillOpacity="0.3"/>
      {/* Lid */}
      <path d="M6 28 Q6 14 32 14 Q58 14 58 28Z" fill="#8B5E38"/>
      <path d="M6 28 Q6 14 32 14 Q58 14 58 28Z" fill="url(#cw)" fillOpacity="0.2"/>
      {/* Wood grain lines */}
      <line x1="16" y1="28" x2="16" y2="56" stroke="#5A3A1F" strokeWidth="1" opacity="0.5"/>
      <line x1="26" y1="28" x2="26" y2="56" stroke="#5A3A1F" strokeWidth="1" opacity="0.5"/>
      <line x1="38" y1="28" x2="38" y2="56" stroke="#5A3A1F" strokeWidth="1" opacity="0.5"/>
      <line x1="48" y1="28" x2="48" y2="56" stroke="#5A3A1F" strokeWidth="1" opacity="0.5"/>
      {/* Iron bands */}
      <rect x="4" y="37" width="56" height="5" rx="1" fill="#555" stroke="#777" strokeWidth="0.5"/>
      <rect x="4" y="46" width="56" height="5" rx="1" fill="#555" stroke="#777" strokeWidth="0.5"/>
      {/* Lid band */}
      <rect x="4" y="26" width="56" height="4" rx="1" fill="#555" stroke="#777" strokeWidth="0.5"/>
      {/* Lock plate */}
      <rect x="26" y="35" width="12" height="10" rx="2" fill="#B8860B"/>
      {/* Lock hole */}
      <rect x="29" y="38" width="6" height="4" rx="1" fill="#5A3A1F"/>
      {/* Rivet dots */}
      <circle cx="8" cy="39" r="1.5" fill="#888"/>
      <circle cx="56" cy="39" r="1.5" fill="#888"/>
      <circle cx="8" cy="48" r="1.5" fill="#888"/>
      <circle cx="56" cy="48" r="1.5" fill="#888"/>
      <defs>
        <linearGradient id="cw" x1="6" y1="14" x2="58" y2="56" gradientUnits="userSpaceOnUse">
          <stop stopColor="#fff" stopOpacity="0.4"/>
          <stop offset="1" stopColor="#000" stopOpacity="0.2"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

function UncommonChestIcon({ size = 64 }) {
  // WoW-style: stone base, green emerald lock, carved rune border
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Body */}
      <rect x="6" y="30" width="52" height="26" rx="3" fill="#4A5568"/>
      {/* Lid arched */}
      <path d="M6 30 Q6 16 32 16 Q58 16 58 30Z" fill="#556"/>
      {/* Stone texture lines */}
      <path d="M8 38 H56" stroke="#3a3a4a" strokeWidth="0.8" opacity="0.7"/>
      <path d="M8 46 H56" stroke="#3a3a4a" strokeWidth="0.8" opacity="0.7"/>
      <path d="M20 30 V56" stroke="#3a3a4a" strokeWidth="0.8" opacity="0.5"/>
      <path d="M44 30 V56" stroke="#3a3a4a" strokeWidth="0.8" opacity="0.5"/>
      {/* Green rune glow border */}
      <rect x="7" y="31" width="50" height="24" rx="2" stroke="#2ecc71" strokeWidth="1.5" fill="none" opacity="0.6"/>
      <path d="M7 30 Q7 17 32 17 Q57 17 57 30" stroke="#2ecc71" strokeWidth="1.5" fill="none" opacity="0.5"/>
      {/* Metal banding */}
      <rect x="4" y="27" width="56" height="5" rx="1" fill="#667" stroke="#2ecc71" strokeWidth="0.5"/>
      {/* Emerald lock */}
      <path d="M28 38 L32 34 L36 38 L34 46 L30 46Z" fill="#2ecc71"/>
      <path d="M28 38 L32 34 L36 38 L34 46 L30 46Z" fill="url(#egem)"/>
      {/* Gem shine */}
      <path d="M30 35 L32 34 L33 36Z" fill="#fff" opacity="0.5"/>
      {/* Corner runes */}
      <text x="9" y="26" fontSize="7" fill="#2ecc71" opacity="0.7" fontFamily="monospace">᛭</text>
      <text x="49" y="26" fontSize="7" fill="#2ecc71" opacity="0.7" fontFamily="monospace">᛭</text>
      <defs>
        <radialGradient id="egem" cx="50%" cy="35%" r="60%">
          <stop offset="0%" stopColor="#90ff90"/>
          <stop offset="100%" stopColor="#1a7a40"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

function RareChestIcon({ size = 64 }) {
  // Arcane blue: dark ornate chest with sapphire gem, arcane sigils
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Body */}
      <rect x="5" y="29" width="54" height="27" rx="4" fill="#1a2a4a"/>
      {/* Lid */}
      <path d="M5 29 Q5 14 32 14 Q59 14 59 29Z" fill="#1f3461"/>
      {/* Blue filigree on lid */}
      <path d="M14 22 Q32 17 50 22" stroke="#3498db" strokeWidth="1.2" fill="none" opacity="0.8"/>
      <path d="M18 25 Q32 21 46 25" stroke="#3498db" strokeWidth="0.8" fill="none" opacity="0.6"/>
      {/* Arcane rune circles */}
      <circle cx="32" cy="22" r="5" stroke="#3498db" strokeWidth="1" fill="none" opacity="0.5"/>
      <circle cx="32" cy="22" r="3" stroke="#3498db" strokeWidth="0.6" fill="none" opacity="0.3"/>
      {/* Gold trim bands */}
      <rect x="4" y="26" width="56" height="5" rx="1" fill="#2a3a5a" stroke="#3498db" strokeWidth="1"/>
      <rect x="4" y="38" width="56" height="3" rx="1" fill="#1a2a3a" stroke="#3498db" strokeWidth="0.6" opacity="0.7"/>
      <rect x="4" y="48" width="56" height="3" rx="1" fill="#1a2a3a" stroke="#3498db" strokeWidth="0.6" opacity="0.7"/>
      {/* Sapphire gem lock */}
      <path d="M29 36 L32 32 L35 36 L34 42 L30 42Z" fill="#3498db"/>
      <path d="M29 36 L32 32 L35 36 L34 42 L30 42Z" fill="url(#sapphire)"/>
      <path d="M30 33 L32 32 L33 34Z" fill="#fff" opacity="0.5"/>
      {/* Blue glow */}
      <path d="M29 36 L32 32 L35 36 L34 42 L30 42Z" stroke="#60c0ff" strokeWidth="0.8" fill="none"/>
      {/* Corner ornaments */}
      <path d="M8 30 L6 28 L10 28 Z" fill="#3498db" opacity="0.6"/>
      <path d="M56 30 L58 28 L54 28 Z" fill="#3498db" opacity="0.6"/>
      <path d="M8 54 L6 56 L10 56 Z" fill="#3498db" opacity="0.6"/>
      <path d="M56 54 L58 56 L54 56 Z" fill="#3498db" opacity="0.6"/>
      {/* Glow overlay */}
      <rect x="5" y="29" width="54" height="27" rx="4" fill="none" stroke="#3498db" strokeWidth="1.5" opacity="0.4"/>
      <defs>
        <radialGradient id="sapphire" cx="40%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#9ae0ff"/>
          <stop offset="100%" stopColor="#1060b0"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

function EpicChestIcon({ size = 64 }) {
  // WoW epic style: void purple, arcane eye symbol, gold trim with void cracks
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Body */}
      <rect x="4" y="28" width="56" height="28" rx="4" fill="#1a0a2e"/>
      {/* Lid */}
      <path d="M4 28 Q4 12 32 12 Q60 12 60 28Z" fill="#230d3e"/>
      {/* Void cracks */}
      <path d="M20 20 L25 26 L22 28" stroke="#bf5fff" strokeWidth="0.8" opacity="0.7"/>
      <path d="M44 20 L39 26 L42 28" stroke="#bf5fff" strokeWidth="0.8" opacity="0.7"/>
      {/* Gold trim */}
      <rect x="3" y="25" width="58" height="5" rx="1.5" fill="#2a1040" stroke="#bf5fff" strokeWidth="1.2"/>
      <rect x="3" y="38" width="58" height="3" rx="1" fill="#150820" stroke="#bf5fff" strokeWidth="0.7" opacity="0.7"/>
      <rect x="3" y="48" width="58" height="3" rx="1" fill="#150820" stroke="#bf5fff" strokeWidth="0.7" opacity="0.7"/>
      {/* Arcane eye lock */}
      <ellipse cx="32" cy="36" rx="6" ry="4" fill="#1a0a2e" stroke="#bf5fff" strokeWidth="1.2"/>
      <ellipse cx="32" cy="36" rx="3" ry="3" fill="#bf5fff"/>
      <circle cx="32" cy="36" r="1.5" fill="#0a0015"/>
      <ellipse cx="32" cy="36" rx="6" ry="4" stroke="#df9fff" strokeWidth="0.5" fill="none" opacity="0.4"/>
      {/* Void glow on lock */}
      <ellipse cx="32" cy="36" rx="8" ry="6" fill="none" stroke="#bf5fff" strokeWidth="0.5" opacity="0.3"/>
      {/* Corner arcane runes */}
      <path d="M7 29 L4 26 M4 26 L7 26" stroke="#bf5fff" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M57 29 L60 26 M60 26 L57 26" stroke="#bf5fff" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M7 54 L4 57 M4 57 L7 57" stroke="#bf5fff" strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M57 54 L60 57 M60 57 L57 57" stroke="#bf5fff" strokeWidth="1.2" strokeLinecap="round"/>
      {/* Glint */}
      <circle cx="31" cy="35" r="1" fill="#fff" opacity="0.4"/>
      {/* Outer glow */}
      <rect x="4" y="28" width="56" height="28" rx="4" fill="none" stroke="#bf5fff" strokeWidth="1.5" opacity="0.35"/>
      <path d="M4 28 Q4 12 32 12 Q60 12 60 28" fill="none" stroke="#bf5fff" strokeWidth="1.5" opacity="0.35"/>
      <defs>
        <radialGradient id="eyeglow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#df9fff" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="transparent"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

function LegendaryChestIcon({ size = 64 }) {
  // Supreme golden reliquary: WoW legendary orange/gold, ornate sunburst, dragon motif
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Sunburst glow behind chest */}
      {[0,30,60,90,120,150,180,210,240,270,300,330].map((a, i) => (
        <line key={i}
          x1={32 + Math.cos(a * Math.PI / 180) * 8}
          y1={28 + Math.sin(a * Math.PI / 180) * 8}
          x2={32 + Math.cos(a * Math.PI / 180) * 16}
          y2={28 + Math.sin(a * Math.PI / 180) * 16}
          stroke="#FFD700" strokeWidth="1.5" opacity="0.25"
        />
      ))}
      {/* Body — rich gold-plated dark metal */}
      <rect x="5" y="30" width="54" height="26" rx="4" fill="#1a1200"/>
      {/* Body gold plate inlay */}
      <rect x="5" y="30" width="54" height="26" rx="4" fill="url(#lbody)"/>
      {/* Lid */}
      <path d="M5 30 Q5 13 32 13 Q59 13 59 30Z" fill="#1e1400"/>
      <path d="M5 30 Q5 13 32 13 Q59 13 59 30Z" fill="url(#llid)"/>
      {/* Gold banding — thick ornate trim */}
      <rect x="3" y="27" width="58" height="5" rx="2" fill="url(#lband)"/>
      <rect x="3" y="39" width="58" height="4" rx="1.5" fill="url(#lband2)" opacity="0.9"/>
      <rect x="3" y="49" width="58" height="4" rx="1.5" fill="url(#lband2)" opacity="0.9"/>
      {/* Dragon wing filigree */}
      <path d="M10 24 Q16 18 22 22 Q18 25 14 28Z" fill="#FFD700" opacity="0.6"/>
      <path d="M54 24 Q48 18 42 22 Q46 25 50 28Z" fill="#FFD700" opacity="0.6"/>
      {/* Ornate center lock — flame gem */}
      <path d="M32 28 L27 34 L29 42 L32 44 L35 42 L37 34Z" fill="url(#flamegem)"/>
      <path d="M32 28 L27 34 L29 42 L32 44 L35 42 L37 34Z" stroke="#FFD700" strokeWidth="1"/>
      {/* Flame inner */}
      <path d="M32 30 L29 35 L30 40 L32 41 L34 40 L35 35Z" fill="#FF8C00" opacity="0.7"/>
      <path d="M32 31 L30.5 35 L31 39 L32 40 L33 39 L33.5 35Z" fill="#FFD700" opacity="0.5"/>
      <circle cx="32" cy="38" r="2" fill="#fff" opacity="0.25"/>
      {/* Lock gem shine */}
      <path d="M30 30 L32 28 L33 30Z" fill="#fff" opacity="0.5"/>
      {/* Rivet ornaments */}
      <circle cx="9" cy="41" r="2" fill="url(#lrivet)"/>
      <circle cx="55" cy="41" r="2" fill="url(#lrivet)"/>
      <circle cx="9" cy="51" r="2" fill="url(#lrivet)"/>
      <circle cx="55" cy="51" r="2" fill="url(#lrivet)"/>
      {/* Outer glow frame */}
      <rect x="5" y="30" width="54" height="26" rx="4" fill="none" stroke="#FFD700" strokeWidth="1.5" opacity="0.5"/>
      <path d="M5 30 Q5 13 32 13 Q59 13 59 30" fill="none" stroke="#FFD700" strokeWidth="1.5" opacity="0.5"/>
      <defs>
        <linearGradient id="lbody" x1="5" y1="30" x2="59" y2="56" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#8B6914" stopOpacity="0.5"/>
          <stop offset="50%" stopColor="#FFD700" stopOpacity="0.15"/>
          <stop offset="100%" stopColor="#5a4000" stopOpacity="0.4"/>
        </linearGradient>
        <linearGradient id="llid" x1="5" y1="13" x2="59" y2="30" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFD700" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#8B6914" stopOpacity="0.1"/>
        </linearGradient>
        <linearGradient id="lband" x1="0" y1="0" x2="0" y2="5" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FFE55C"/>
          <stop offset="50%" stopColor="#B8860B"/>
          <stop offset="100%" stopColor="#FFE55C"/>
        </linearGradient>
        <linearGradient id="lband2" x1="0" y1="0" x2="0" y2="4" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#D4A017"/>
          <stop offset="50%" stopColor="#8B6914"/>
          <stop offset="100%" stopColor="#D4A017"/>
        </linearGradient>
        <linearGradient id="flamegem" x1="32" y1="28" x2="32" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FF6600"/>
          <stop offset="50%" stopColor="#FF8C00"/>
          <stop offset="100%" stopColor="#FFD700"/>
        </linearGradient>
        <radialGradient id="lrivet" cx="40%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#FFE55C"/>
          <stop offset="100%" stopColor="#8B6914"/>
        </radialGradient>
      </defs>
    </svg>
  );
}

const CHEST_ICONS = {
  common:    CommonChestIcon,
  uncommon:  UncommonChestIcon,
  rare:      RareChestIcon,
  epic:      EpicChestIcon,
  legendary: LegendaryChestIcon,
};

const TABS = ['Games', 'Chests', 'Themes', 'Borders', 'Buy Streams', 'Tasks'];

// ── Games Tab (embedded from Games page) ─────────────────────────────────────
const SHOP_DAILY_CARD = {
  id: 'daily',
  title: 'Daily Bonus',
  desc: () => <>Free • Streak rewards • Up to 31 days</>,
  maxWin: null,
  icon: '📅',
  accentColor: 'var(--rarity-uncommon)',
  route: '/daily-bonus',
  cost: 0,
};

const SHOP_GAME_CARDS = [
  {
    id: 'slots',
    title: 'Slot Machine',
    desc: (s) => <>325{s()} / spin</>,
    maxWin: 10000,
    icon: '🎰',
    accentColor: 'var(--brand)',
    component: SlotMachine,
    cost: 325,
  },
  {
    id: 'spin',
    title: 'Spin Wheel',
    desc: (s) => <>450{s()} / spin • 10 slices</>,
    maxWin: 1000,
    icon: '🎡',
    accentColor: 'var(--accent)',
    component: SpinWheel,
    cost: 450,
  },
  {
    id: 'scratch',
    title: 'Scratch Card',
    desc: (s) => <>50{s()} / card • Match 3 to win</>,
    maxWin: 5000,
    icon: '🎟️',
    accentColor: 'var(--rarity-epic)',
    component: ScratchCard,
    cost: 50,
  },
  {
    id: 'memory',
    title: 'Memory Game',
    desc: () => <>Free • 4 rounds • 60s → 15s</>,
    maxWin: 375,
    icon: '🧠',
    accentColor: 'var(--brand)',
    component: MemoryGame,
    cost: 0,
  },
];

function GamesTab() {
  const [activeGame, setActiveGame] = useState(null);
  const [userData, setUserData]     = useState(null);
  const [insufficientGame, setInsuff] = useState(null);
  const navigate = useNavigate();
  const { showSilentAd } = useAdManager();
  const unsubRef = useRef(null);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      unsubRef.current?.();
      if (!user) return;
      unsubRef.current = onSnapshot(doc(db, 'users', user.uid), (snap) => {
        if (snap.exists()) setUserData(snap.data());
      });
    });
    return () => { unsubAuth(); unsubRef.current?.(); };
  }, []);

  const active = SHOP_GAME_CARDS.find(g => g.id === activeGame);
  const balance = userData?.totalStreams || 0;

  const handleCloseGame = () => {
    setActiveGame(null);
    showSilentAd();
  };

  const handleOpenGame = (game) => {
    if (game.locked) return;
    if (game.route) { navigate(game.route); return; }
    const isDev = userData?.isDev;
    if (!isDev && game.cost > 0) {
      if (balance < game.cost) {
        setInsuff(game.id);
        setTimeout(() => setInsuff(null), 2500);
        return;
      }
    }
    setActiveGame(game.id);
  };

  return (
    <div style={{ position: 'relative' }}>
      {insufficientGame && (
        <div className="games__insufficient">
          <STokenLogo size={14} />
          <span>Not enough Streams to play</span>
        </div>
      )}

      <div className="games__grid">
        {[SHOP_DAILY_CARD, ...SHOP_GAME_CARDS].map(game => {
          const canAfford = userData?.isDev || game.cost === 0 || balance >= game.cost;
          return (
            <button
              key={game.id}
              className={`card games__card${game.locked ? ' games__card--locked' : ''}${!canAfford ? ' games__card--broke' : ''}`}
              style={{ '--game-color': game.accentColor }}
              onClick={() => handleOpenGame(game)}
              disabled={game.locked}
              aria-label={game.title}
            >
              <div className="games__card-icon">{game.icon}</div>
              <div className="games__card-info">
                <div className="games__card-title">{game.title}</div>
                <div className="games__card-desc" style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {game.desc(() => <STokenLogo size={11} />)}
                </div>
                {game.maxWin && (
                  <div className="games__card-maxwin">
                    <span>Max win</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                      {game.maxWin.toLocaleString()}<STokenLogo size={10} />
                    </span>
                  </div>
                )}
              </div>
              {!game.locked && <div className="games__card-arrow">›</div>}
              {game.locked && <div className="games__card-lock">🔒</div>}
              <div className="games__card-glow" />
            </button>
          );
        })}
      </div>

      {activeGame && active?.component && (
        <div className="games__modal-overlay" onClick={handleCloseGame}>
          <div className="games__modal" onClick={e => e.stopPropagation()}>
            <button className="games__modal-close" onClick={handleCloseGame} aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <active.component onClose={handleCloseGame} />
          </div>
        </div>
      )}
    </div>
  );
}

const CHESTS = [
  { id: 'common',    name: 'Common Chest',    price: 185,  color: '#95a5a6', description: 'Common to Rare rewards' },
  { id: 'uncommon',  name: 'Uncommon Chest',  price: 350,  color: '#2ecc71', description: 'Uncommon to Epic rewards' },
  { id: 'rare',      name: 'Rare Chest',      price: 485,  color: '#3498db', description: 'Rare to Legendary rewards' },
  { id: 'epic',      name: 'Epic Chest',      price: 695,  color: '#bf5fff', description: 'Epic to Ultimate rewards' },
  { id: 'legendary', name: 'Legendary Chest', price: 1150, color: '#FFD700', description: 'Legendary jackpot rewards' },
];

const BORDERS = [
  {
    id: 'ember',
    name: 'Ember Ring',
    desc: 'Warm amber & gold',
    gradient: 'linear-gradient(135deg, #FF8C42, #FFD700, #FF8C42)',
    price: 500,
  },
  {
    id: 'frost',
    name: 'Frost Halo',
    desc: 'Icy cyan & blue',
    gradient: 'linear-gradient(135deg, #00D4FF, #0066FF, #00D4FF)',
    price: 750,
  },
  {
    id: 'phantom',
    name: 'Phantom Aura',
    desc: 'Mystic purple & pink',
    gradient: 'linear-gradient(135deg, #A855F7, #EC4899, #A855F7)',
    price: 750,
  },
  {
    id: 'inferno',
    name: 'Inferno Crown',
    desc: 'Blazing red & orange',
    gradient: 'linear-gradient(135deg, #FF0000, #FF4500, #FFD700, #FF4500, #FF0000)',
    price: 1000,
    animated: true,
  },
  {
    id: 'galaxy',
    name: 'Galaxy Halo',
    desc: 'Shifting cosmic spectrum',
    gradient: 'linear-gradient(135deg, #FF006E, #8338EC, #3A86FF, #00D4FF, #8338EC, #FF006E)',
    price: 1500,
    animated: true,
  },
];

// ── Chest Opening Modal ───────────────────────────────────────────────────────
function ChestOpeningModal({ chest, onClose }) {
  const [phase, setPhase] = useState('idle');   // idle | opening | reveal | error
  const [reward, setReward] = useState(null);
  const [errMsg, setErrMsg] = useState('');

  const open = async () => {
    setPhase('opening');
    try {
      const { data } = await httpsCallable(functions, 'openChest')({ chestId: chest.id });
      await new Promise(r => setTimeout(r, 1400)); // let animation breathe
      setReward(data);
      setPhase('reveal');
    } catch (e) {
      const msg = e?.message?.includes('NOT_ENOUGH_STREAMS')
        ? 'Not enough Streams!'
        : 'Something went wrong. Try again.';
      setErrMsg(msg);
      setPhase('error');
    }
  };

  return (
    <div className="chest-modal__overlay" onClick={phase === 'reveal' ? onClose : undefined}>
      <div className="chest-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="chest-modal__header" style={{ '--chest-color': chest.color }}>
          <span className="chest-modal__icon">
            {React.createElement(CHEST_ICONS[chest.id], { size: 36 })}
          </span>
          <span className="chest-modal__name">{chest.name}</span>
        </div>

        {phase === 'idle' && (
          <div className="chest-modal__body">
            <p className="chest-modal__hint">Tap open to spend <strong style={{ display:'inline-flex', alignItems:'center', gap:3 }}>{chest.price.toLocaleString()} <STokenLogo size={13} /></strong> Streams and reveal your reward.</p>
            <div className="chest-modal__actions">
              <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
              <button className="btn btn--primary" style={{ '--btn-color': chest.color }} onClick={open}>
                Open · <STokenLogo size={14} />{chest.price.toLocaleString()}
              </button>
            </div>
          </div>
        )}

        {phase === 'opening' && (
          <div className="chest-modal__body chest-modal__body--center">
            <div className="chest-modal__opening-icon" style={{ '--chest-color': chest.color }}>
              {React.createElement(CHEST_ICONS[chest.id], { size: 72 })}
            </div>
            <p className="chest-modal__opening-text">Opening…</p>
          </div>
        )}

        {phase === 'reveal' && reward && (
          <div className="chest-modal__body chest-modal__body--center">
            <div className="chest-modal__burst" style={{ '--chest-color': chest.color }} aria-hidden="true" />
            <div className="chest-modal__reward-amount">
              <STokenLogo size={32} />
              <span className="chest-modal__reward-num">+{reward.reward.toLocaleString()}</span>
            </div>
            <p className="chest-modal__reward-sub">+{reward.xp} XP · net {reward.net >= 0 ? '+' : ''}{reward.net.toLocaleString()} Streams</p>
            <button className="btn btn--primary btn--full" style={{ marginTop: 'var(--sp-4)' }} onClick={onClose}>
              Collect
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="chest-modal__body chest-modal__body--center">
            <p className="chest-modal__err">{errMsg}</p>
            <div className="chest-modal__actions">
              <button className="btn btn--ghost" onClick={onClose}>Close</button>
              <button className="btn btn--primary" onClick={() => setPhase('idle')}>Retry</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Borders Tab ───────────────────────────────────────────────────────────────
function BordersTab() {
  const [userData, setUserData] = useState(null);
  const [buying, setBuying]     = useState(null);
  const [applying, setApplying] = useState(null);
  const [toast, setToast]       = useState(null);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), snap => setUserData(snap.data()));
    return unsub;
  }, []);

  const unlocked = userData?.unlockedBorders || [];
  const active   = userData?.activeBorder    || null;
  const streams  = userData?.totalStreams     || 0;

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const buyBorder = async (border) => {
    if (buying) return;
    if (streams < border.price) {
      showToast('err', `Need ${border.price.toLocaleString()} Streams.`);
      return;
    }
    setBuying(border.id);
    try {
      await httpsCallable(functions, 'buyBorder')({ borderId: border.id });
      showToast('win', `${border.name} unlocked!`);
    } catch (e) {
      const msg = e?.message?.includes('NOT_ENOUGH_STREAMS') ? 'Not enough Streams.'
        : e?.message?.includes('ALREADY_OWNED') ? 'Already owned.'
        : 'Purchase failed. Try again.';
      showToast('err', msg);
    } finally {
      setBuying(null);
    }
  };

  const applyBorder = async (borderId) => {
    if (applying) return;
    const newVal = active === borderId ? null : borderId;
    setApplying(borderId);
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { activeBorder: newVal });
    } catch (e) {
      console.error('[BorderApply]', e);
    } finally {
      setApplying(null);
    }
  };

  return (
    <div className="shop-borders">
      {toast && (
        <div className={`buy-streams__toast buy-streams__toast--${toast.type}`}>{toast.text}</div>
      )}

      <div className="shop-borders__grid">
        {BORDERS.map(border => {
          const owned   = unlocked.includes(border.id);
          const isActive = active === border.id;
          const isLoading = buying === border.id || applying === border.id;

          return (
            <div key={border.id} className={`card shop-borders__card${isActive ? ' shop-borders__card--active' : ''}`}>
              {/* Preview ring */}
              <div className="shop-borders__preview-wrap">
                <div
                  className={`shop-borders__ring${border.animated ? ` shop-borders__ring--${border.id}` : ''}`}
                  style={{ '--border-grad': border.gradient }}
                >
                  <div className="shop-borders__ring-inner">
                    <span className="shop-borders__ring-letter">A</span>
                  </div>
                </div>
              </div>

              <div className="shop-borders__info">
                <div className="shop-borders__name">
                  {border.name}
                  {border.animated && <span className="badge badge--accent" style={{ fontSize: 9, padding: '1px 5px', marginLeft: 4 }}>ANIMATED</span>}
                </div>
                <div className="shop-borders__desc">{border.desc}</div>
              </div>

              {owned ? (
                <button
                  className={`btn shop-borders__btn${isActive ? ' shop-borders__btn--active' : ''}`}
                  style={{ '--border-color': border.gradient }}
                  onClick={() => applyBorder(border.id)}
                  disabled={isLoading}
                >
                  {isLoading ? '…' : isActive ? 'Active' : 'Apply'}
                </button>
              ) : (
                <button
                  className="btn btn--primary shop-borders__btn"
                  onClick={() => buyBorder(border)}
                  disabled={!!buying || streams < border.price}
                >
                  {isLoading ? '…' : (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
                      {border.price.toLocaleString()}<STokenLogo size={13} />
                    </span>
                  )}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Shop() {
  const [activeTab, setActiveTab] = useState('Games');
  const [openingChest, setOpeningChest] = useState(null);
  const navigate = useNavigate();

  return (
    <div className="page shop">
      <div className="grid-bg" aria-hidden="true" />

      <header className="shop__header">
        <button className="page-back-btn" onClick={() => navigate('/')}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
          Home
        </button>
        <h1 className="shop__title">Shop</h1>
        <p className="shop__subtitle">Spend your Streams wisely.</p>
      </header>

      <div className="shop__tabs" role="tablist">
        {TABS.map(tab => (
          <button
            key={tab}
            role="tab"
            aria-selected={activeTab === tab}
            className={`shop__tab${activeTab === tab ? ' shop__tab--active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {openingChest && (
        <ChestOpeningModal chest={openingChest} onClose={() => setOpeningChest(null)} />
      )}

      {activeTab === 'Chests' && (
        <div className="shop__grid">
          {CHESTS.map(chest => (
            <div
              key={chest.id}
              className="card shop__item"
              style={{ '--item-color': chest.color }}
            >
              <div className="shop__item-icon">
                {React.createElement(CHEST_ICONS[chest.id], { size: 56 })}
              </div>
              <div className="shop__item-name">{chest.name}</div>
              <div className="shop__item-desc">{chest.description}</div>
              <button
                className="btn shop__item-btn"
                onClick={() => setOpeningChest(chest)}
              >
                <STokenLogo size={16} />
                {chest.price.toLocaleString()}
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'Borders' && <BordersTab />}

      {activeTab === 'Themes' && <ThemesTab />}

      {activeTab === 'Games' && <GamesTab />}

      {activeTab === 'Buy Streams' && <BuyStreamsTab />}

      {activeTab === 'Tasks' && (
        <div className="tasks-cs">
          <div className="tasks-cs__hero">
            <div className="tasks-cs__scan-lines" aria-hidden="true" />
            <div className="tasks-cs__corner tasks-cs__corner--tl" aria-hidden="true" />
            <div className="tasks-cs__corner tasks-cs__corner--tr" aria-hidden="true" />
            <div className="tasks-cs__corner tasks-cs__corner--bl" aria-hidden="true" />
            <div className="tasks-cs__corner tasks-cs__corner--br" aria-hidden="true" />

            <div className="tasks-cs__icon" aria-hidden="true">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect x="4" y="10" width="40" height="28" rx="3" stroke="var(--brand)" strokeWidth="2" strokeDasharray="4 2"/>
                <path d="M14 22l6 6 14-10" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="38" cy="12" r="5" fill="var(--bg-base)" stroke="var(--accent)" strokeWidth="1.5"/>
                <path d="M38 9.5v5M35.5 12h5" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <div className="tasks-cs__icon-glow" aria-hidden="true" />
            </div>

            <div className="tasks-cs__label">SYSTEM MODULE</div>
            <h2 className="tasks-cs__title">
              <span className="tasks-cs__title-glitch" data-text="TASK NETWORK">TASK NETWORK</span>
            </h2>
            <div className="tasks-cs__status-row">
              <span className="tasks-cs__status-dot" aria-hidden="true" />
              <span className="tasks-cs__status-text">INITIALIZING</span>
              <span className="tasks-cs__status-bar" aria-hidden="true">
                <span className="tasks-cs__status-fill" />
              </span>
            </div>
          </div>

          <div className="tasks-cs__info-grid">
            <div className="tasks-cs__info-card">
              <div className="tasks-cs__info-icon">◈</div>
              <div className="tasks-cs__info-title">Complete Offers</div>
              <div className="tasks-cs__info-body">Sign up for apps, try free trials, answer surveys — get Streams deposited instantly on completion.</div>
            </div>
            <div className="tasks-cs__info-card">
              <div className="tasks-cs__info-icon">◈</div>
              <div className="tasks-cs__info-title">Watch Ads</div>
              <div className="tasks-cs__info-body">30-second rewarded videos. Skip the noise or earn bonus Streams — your call, every time.</div>
            </div>
            <div className="tasks-cs__info-card">
              <div className="tasks-cs__info-icon">◈</div>
              <div className="tasks-cs__info-title">Install &amp; Play</div>
              <div className="tasks-cs__info-body">Download featured games, reach a milestone, and collect a one-time high-value Stream payout.</div>
            </div>
          </div>

          <div className="tasks-cs__notice">
            <span className="tasks-cs__notice-tag">&#47;&#47; NOTE</span>
            {' '}Tasks are a fast-track way to boost your balance on top of passive earning. High-value offers can credit thousands of Streams in a single session. The network launches soon — existing users will be notified first.
          </div>
        </div>
      )}

    </div>
  );
}
